import { Injectable } from '@angular/core';
import { UserService } from '@services/user.service';
import { Subject, of, Observable } from 'rxjs';
import { environment } from '@env/environment';
import { HttpUtilService } from '@services/http-util.service';
import { ItemsService } from './items.service';
import { ScaffoldSidebarService } from '@app/framework/sidebar/scaffold-sidebar.service';
import { ScaffoldDialogRef } from '@app/framework/dialog/scaffold-dialog.service';
import { take } from 'rxjs/operators';
import { Info } from '../view/rendered/rendered.service';

/**
 * A user item.
 *
 */

export class UserItem {
  /** The username */
  username: string;
  /** The identifier */
  id: number;
  /** The name */
  name: string;
}

/**
 * A comment item.
 */

export class CommentItem {
  /** ID of comment*/
  id?: number;
  /** The comment text */
  text: string;
  /** The selection of test the comment is associated with */
  selection?: string;
  /** The identifier in the html that be used to associate the comment to the selection */
  selection_id?: string;
  /** An array of user mentions in the comment*/
  mentions: Array<string> = [];
  /**Id of a comment this one respondes to*/
  response_to?;
}

@Injectable({
  providedIn: 'root',
})
export class CommentingService {
  /** data item details */
  info: Info;
  /** The active comments target key*/
  active_comments: string;
  /**Subject to get updated active comments*/
  active_change: Subject<any> = new Subject();
  /** The label for the active comments*/
  label: string;
  /** The comments storage object*/
  comments = {
    userFilter: null,
  };
  /** subscription for comment updates */
  observe: Subject<CommentItem[]> = new Subject();
  /**should comments be visible*/
  show_comments = true;
  /** comment update notification for updating comment icons in the UI*/
  _commentNotification: Subject<any> = new Subject();
  /** Sub item for special comnment concideration*/
  sub: Subject<any>;
  /**array of ids to filter base comments by*/
  filter;
  item;
  /**String filter for user*/
  userFilter;
  _sidebar: ScaffoldDialogRef;

  constructor(
    private userService: UserService,
    private http: HttpUtilService,
    private itemsService: ItemsService,
    private sidebar: ScaffoldSidebarService
  ) {}

  /**
   * Sets an active comment target
   * @param     info            The data item.
   * @param     key             The key for the comments.
   * @param     label           (Optional) The label to use on the comment panel.
   * @param     subItem         (Optional) The subItem in a scaffold editor the comment is targeting
   *
   */

  setActive(
    info: Info,
    key: string,
    label?: string,
    subItem?: Subject<any>,
    details?,
    sidebar?
  ) {
    console.log('Set Active', details);
    setTimeout(() => {
      this.info = info;
      this.active_comments = key;
      this.active_change.next(this.active_comments);
      this.label = label;
      this.sub = subItem;
      if (details) {
        this.filter = details.filter;
        this.item = details.item;
      } else {
        this.filter = undefined;
        this.item = undefined;
      }
      this.userFilter = null;
      console.log(this);

      if (this._sidebar) {
        this._sidebar.close();
        this._sidebar = null;
      }
      this._sidebar = sidebar;
    });
  }
  /**Subscription to watch for changes to the active conmments */
  watchActive(): Observable<any> {
    return this.active_change.asObservable();
  }

  /**
   * Stores a comment array
   * @param     id      The id of the data item.
   * @param     key     The key that comments target.
   * @param     data    An array containing all the comments if id/key.
   *
   */

  private _storeComment(id, key: string, data: Array<CommentItem>) {
    if (!this.comments[id]) {
      this.comments[id] = {};
    }
    this.comments[id][key] = data;
    console.log(this.comments);
  }

  /**
   * Get a list of users for the current institution
   *
   */

  getUsers(): Observable<[any]> {
    const url = environment.apiRoot + '/comments/user_search';
    return this.http.request('GET', url, null, null, true);
  }

  /**
   * Applies a filter to the comments based on a user
   *
   * @param filter string user string filter;
   */
  filterUser(filter: string) {
    if (filter && filter.length === 0) {
      filter = null;
    }
    this.userFilter = filter;
    this.comments.userFilter = filter;
    this._commentNotification.next(this.comments);
  }

  /**
   * Internal subscription to get comments API, used for processing in service then as source
   * for external observable
   *
   *
   * @param     info    Data item caontaining id.
   * @param     key     Paramete the comments target.
   */

  private _getComments(info: Info, key: string): Observable<CommentItem[]> {
    if (info) {
      if (key) {
        return this.http.request(
          'GET',
          environment.apiRoot + '/comments/' + info.id + '/' + key,
          null,
          null,
          true
        );
      } else {
        return this.http.request(
          'GET',
          environment.apiRoot + '/comments/' + info.id,
          null,
          null,
          true
        );
      }
    }
    return of([]);
  }

  /**
   * Gets all mentions in the comment body and adds theme to an array for use in the API
   *
   * @param     comment  CommentItem to process.
   *
   */

  _getMentions(comment: CommentItem) {
    const comment_object = comment;
    comment_object.mentions = [];
    console.log(comment);
    const temp_el = document.createElement('div');
    temp_el.innerHTML = comment.text;
    const mentions = temp_el.getElementsByClassName('comment-mention');
    for (let i = 0; i < mentions.length; i++) {
      const mention = mentions.item(i);
      comment_object.mentions.push(mention.getAttribute('data-mention'));
    }
    return comment_object;
  }

  /**
   * External subscription for getting all comments on a key for a data item
   *
   * @param     info    (Optional) Data item info to subscribe to
   * @param     key     (Optional) The key tyhe comment data is associated with
   */

  getComments(info?: Info, key?: string): Observable<CommentItem[]> {
    if (!info) {
      info = this.info;
    }
    if (!info) {
      return of([]);
    }
    if (!key) {
      key = this.active_comments;
    }
    this.observe = new Subject();
    this._getComments(info, key)
      .pipe(take(1))
      .subscribe((e: Array<CommentItem>) => {
        this._storeComment(info.id, key, e);
        const com: any = { userFilter: this.userFilter };
        com[info.id] = {};
        com[info.id][key] = e;
        this._commentNotification.next(com);
        this.observe.next(e);
        console.log(e);
      });

    return this.observe.asObservable();
  }

  /**
   * Gets comments update observable
   */

  getCommentsUpdates(): Observable<any> {
    return this._commentNotification.asObservable();
  }

  /**
   * Adds a comment to the current key on current data item
   * @param     comment   The comment data.
   * @param     reply     (Optional) The Id of the comment to reply too.
   *
   * @param target
   * @param name
   */

  addComment(
    comment: CommentItem,
    reply?,
    target?,
    name?
  ): Observable<CommentItem[]> {
    const observe = new Subject();
    if (!target) {
      target = this.info.id;
    }
    if (!name) {
      name = this.active_comments;
    }
    this.http
      .request(
        'POST',
        environment.apiRoot + '/comments/' + target + '/' + name,
        { data: this._getMentions(comment), reply: reply, label: this.label },
        null,
        true
      )
      .subscribe(e => {
        console.log(this.sub);
        if (this.sub) {
          this.sub.next(e);
        }
        // if there is a comment selection, save the value of the text editor so the link isn't lost
        if (comment.selection_id) {
          this.itemsService.saveData(target, name).subscribe(() => comment);
        }
        this._getComments({ id: target, type: null }, name)
          .pipe(take(1))
          .subscribe(c => {
            const temp_coms = {},
              final_coms = [];
            c.forEach(comm => (temp_coms[comm.id] = comm));
            c.forEach(comm => {
              if (comm.response_to) {
                console.log('Has response', comm.response_to);
                if (temp_coms[comm.response_to]) {
                  if (!temp_coms[comm.response_to].replies) {
                    temp_coms[comm.response_to].replies = [];
                  }
                  temp_coms[comm.response_to].replies.push(comm);
                }
              } else {
                final_coms.push(comm);
              }
            });
            this._storeComment(target, name, c);
            const com: any = { userFilter: this.userFilter };
            com[target] = {};
            com[target][name] = c;
            this._commentNotification.next(com);
            console.log(final_coms);
            this.observe.next(c);
            observe.next(c);
          });
      });
    return this.observe.asObservable();
  }
  /**
   * Make an update to a comment
   * @param comment the comment to update
   * @param data data containing the update
   */
  updateComment(comment, data) {
    const observe = new Subject();
    const target = comment.target,
      name = comment.key;
    this.http
      .request(
        'PUT',
        environment.apiRoot + '/comments/' + comment.id,
        data,
        null,
        true
      )
      .subscribe(_e => {
        // if there is a comment selection, save the value of the text editor so the link isn't lost
        this._getComments({ id: target, type: null }, name)
          .pipe(take(1))
          .subscribe(c => {
            const temp_coms = {},
              final_coms = [];
            c.forEach(comm => (temp_coms[comm.id] = comm));
            c.forEach(comm => {
              if (comm.response_to) {
                console.log('Has response', comm.response_to);
                if (temp_coms[comm.response_to]) {
                  if (!temp_coms[comm.response_to].replies) {
                    temp_coms[comm.response_to].replies = [];
                  }
                  temp_coms[comm.response_to].replies.push(comm);
                }
              } else {
                final_coms.push(comm);
              }
            });
            this._storeComment(target, name, c);
            const com: any = { userFilter: this.userFilter };
            com[target] = {};
            com[target][name] = c;
            this._commentNotification.next(com);
            console.log(final_coms);
            this.observe.next(c);
            observe.next(c);
          });
      });
    return this.observe.asObservable();
  }
  /**
   * Mark a comment as resolved
   * @param comment the momment to update
   */
  resolveComment(comment) {
    const observe = new Subject();
    const target = comment.target,
      name = comment.key;
    this.http
      .request(
        'DELETE',
        environment.apiRoot + '/comments/' + comment.id,
        null,
        null,
        true
      )
      .subscribe((_e): void => {
        // if there is a comment selection, save the value of the text editor so the link isn't lost

        this._getComments({ id: target, type: null }, name)
          .pipe(take(1))
          .subscribe(c => {
            const temp_coms = {},
              final_coms = [];
            c.forEach(comm => (temp_coms[comm.id] = comm));
            c.forEach(comm => {
              if (comm.response_to) {
                console.log('Has response', comm.response_to);
                if (temp_coms[comm.response_to]) {
                  if (!temp_coms[comm.response_to].replies) {
                    temp_coms[comm.response_to].replies = [];
                  }
                  temp_coms[comm.response_to].replies.push(comm);
                }
              } else {
                final_coms.push(comm);
              }
            });
            this._storeComment(target, name, c);
            const com: any = { userFilter: this.userFilter };
            com[target] = {};
            com[target][name] = c;
            this._commentNotification.next(com);
            console.log(final_coms);
            this.observe.next(c);
            observe.next(c);
          });
      });
    return this.observe.asObservable();
  }
  /**
   *Get a list of all mentions for the current user
   * */
  userMentions() {
    return this.http.request(
      'GET',
      environment.apiRoot + '/mentions/users',
      null,
      null,
      true
    );
  }
  /**
   * Sets the read statre of a mention in the database
   * @param read Read state
   * @param mention id of mention to set
   */
  setMentionRead(read: boolean, mention) {
    return this.http.request(
      'PUT',
      environment.apiRoot +
        '/mentions/' +
        (read ? 'read' : 'unread') +
        '/' +
        mention,
      null,
      null,
      true
    );
  }
}
